<template>
  <div
    :id="`${source}_attachment_renderer`"
    :class="['flex', rendererDirection, sectionCSS]"
    :style="`gap: ${gap}px;`"
  >
    <template v-for="(attachment, index) in attachments" :key="`${attachment?.id}_attachment_${index}`">
      <div
        v-if="isMediaAttachment(attachment)"
        class="media_attachment_wrapper"
      >
        <component
          :is="getDisplayComponent(attachment)"
          :attachment="attachment"
          :source="source"
          :icon-size="iconSize"
          :type="type"
          :caption="caption"
        />
      </div>

      <div
        v-else
        class="relative flex flex-row items-center gap-3 justify-between rounded-xl cursor-pointer w-[10vw] md:w-[20vw] shrink-0 attachment_container group overflow-visible"
        :style="`padding: ${padding}px; background: ${backgroundColor}`"
        @click="$emit('attachment-click', attachment)"
      >
        <div class="flex flex-row items-center gap-2 truncate w-full">
          <component
            :is="getDisplayComponent(attachment)"
            :attachment="attachment"
            :source="source"
            :icon-size="iconSize"
            :type="type"
          />

          <span
            v-if="showAttachmentName(attachment)"
            class="text-sm font-normal primary_color truncate"
          >
            {{ attachment?.file_name }}
          </span>
        </div>

        <div
          id="attachment_renderer_actions"
          v-if="showActionsSection && !isMediaAttachment(attachment)"
          class="flex-row gap-2 items-center rounded-lg px-1 py-[2px] border_8 flex-shrink-0 bg_color hidden group-hover:flex"
          @click.stop
        >
          <div class="flex flex-row items-center gap-1">
            <a-tooltip
              v-for="(action, actionIndex) in computedActions"
              :key="`${action?.id}_action_${actionIndex}`"
              placement="top"
              color="var(--tooltip-bg)"
              :title="action?.tooltip ? action?.text : ''"
            >
              <div
                class="flex items-center justify-center cursor-pointer p-1 rounded-[6px] action_container"
                :style="`min-width: ${action?.size + 8}px; min-height: ${action?.size + 8}px;`"
                @click.stop="action?.action(attachment)"
              >
                <ThemeImage
                  :style="`width: ${action?.size}px; height: ${action?.size}px; flex-shrink: 0;`"
                  :icon="action?.unselected"
                  :alt="`${action?.id}_icon`"
                />
              </div>
            </a-tooltip>

            <CustomDropdown
              v-if="computedExtraActions?.length > 0"
              ref="dropdownRef"
              maxHeight="280px"
              gap="10px"
              position="end"
              dropdownCSS="p-0"
              :hover-area-needed="false"
              :relative-to-viewport="true"
            >
              <template #trigger>
                <div
                  class="flex items-center justify-center cursor-pointer p-1 rounded-[6px] action_container"
                  style="min-width: 24px; min-height: 24px"
                  @click.stop
                >
                  <ThemeImage
                    class="size-4 flex-shrink-0"
                    :icon="imageNames.MORE_VERTICAL"
                    alt="more_vertical_three_dots"
                  />
                </div>
              </template>

              <template #default>
                <div class="flex flex-col gap-2 p-2">
                  <div
                    v-for="(action, extraIndex) in computedExtraActions"
                    :key="`${action?.id}_extra_action_${extraIndex}`"
                    class="dropdown_item cursor-pointer"
                    @click.stop="action?.action(attachment)"
                  >
                    <div
                      class="flex items-center justify-center flex-shrink-0"
                      :style="`min-width: ${action?.size || 16}px; min-height: ${action?.size || 16}px;`"
                    >
                      <ThemeImage
                        :style="`width: ${action?.size || 16}px; height: ${action?.size || 16}px; flex-shrink: 0;`"
                        :icon="action?.icon"
                        :alt="`${action?.id}_icon`"
                      />
                    </div>

                    <span>{{ action?.text }}</span>
                  </div>
                </div>
              </template>
            </CustomDropdown>
          </div>
        </div>

        <div
          v-if="type !== attachmentRendererTypeRef.PREVIEW"
          class="absolute -top-1 -right-1"
          style="z-index: 999"
        >
          <CustomLoader
            v-if="attachment?.isLoading"
            :size="14"
            :border-width="2"
            :show-label="false"
          />

          <div
            v-else
            class="items-center justify-center size-4 cursor-pointer remove_button hidden group-hover:flex"
            @click.stop="$emit('remove-attachment', attachment)"
          >
            <ThemeImage
              :icon="imageNames.CLOSE_VARIANT1"
              class="size-3"
              alt="remove_attachment"
            />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { customSelectorDirections } from "@/src/constants/customComponentConstants.js";
import {
  attachmentDisplayType,
  attachmentRendererType,
  getAttachmentDisplayType,
} from "@/src/constants/attachmentConstants.js";
import { attachmentComponentMapping } from "@/src/constants/attachmentComponentMapping.js";
import CustomLoader from "@/src/components/CustomLoader.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import CustomDropdown from "@/src/components/CustomDropdown.vue";

const attachmentRendererTypeRef = attachmentRendererType;
const attachmentDisplayTypeRef = attachmentDisplayType;

const props = defineProps({
  source: { type: String, required: true },
  gap: { type: Number, default: 6 },
  sectionCSS: { type: String, default: "" },
  padding: { type: Number, default: 8 },
  iconSize: { type: Number, default: 20 },
  direction: { type: String, default: customSelectorDirections.HORIZONTAL },
  attachments: { type: Array, required: true },
  backgroundColor: { type: String, default: "transparent" },
  actions: {
    type: Object,
    default: () => ({ default: [], extra: [] }),
  },
  type: { type: String, default: attachmentRendererType.UPLOAD },
  caption: { type: String, default: "" },
});

const emit = defineEmits(["remove-attachment", "attachment-click"]);

const dropdownRef = ref(null);

const showActionsSection = computed(() => {
  const hasDefaultActions =
    computedActions.value && computedActions.value.length > 0;
  const hasExtraActions =
    computedExtraActions.value && computedExtraActions.value.length > 0;
  return (
    (hasDefaultActions || hasExtraActions) &&
    props.type !== attachmentRendererTypeRef.UPLOAD
  );
});

const rendererDirection = computed(() => {
  return props.direction === customSelectorDirections.HORIZONTAL
    ? "flex-row items-center overflow-x-auto hide-scrollbar"
    : "flex-col justify-center";
});

const computedActions = computed(() => {
  return props.actions?.default || [];
});

const computedExtraActions = computed(() => {
  return props.actions?.extra || [];
});

const showAttachmentName = (attachment) => {
  try {
    const displayType = getDisplayType(attachment);
    if (displayType === attachmentDisplayTypeRef.AUDIO) {
      return props.type === attachmentRendererTypeRef.UPLOAD;
    }
    return true;
  } catch (error) {
    console.error(
      "An error occurred while checking to show the attachment name or not: ",
      error,
    );
    return true;
  }
};

const isMediaAttachment = (attachment) => {
  try {
    if (props.type === attachmentRendererTypeRef.UPLOAD) {
      return false;
    }
    
    const displayType = getDisplayType(attachment);
    return (
      displayType === attachmentDisplayTypeRef.IMAGE ||
      displayType === attachmentDisplayTypeRef.VIDEO
    );
  } catch (error) {
    console.error("An error occurred while checking if media attachment:", error);
    return false;
  }
};

const getDisplayType = (attachment) => {
  try {
    return getAttachmentDisplayType(attachment?.file_name, attachment?.content_type);
  } catch (error) {
    console.error("An error occurred while getting display type: ", error);
    return attachmentDisplayType.FILE;
  }
};

const getDisplayComponent = (attachment) => {
  try {
    const displayType = getAttachmentDisplayType(attachment?.file_name, attachment?.content_type);
    return attachmentComponentMapping[displayType]?.[props.type] || null;
  } catch (error) {
    console.error("An error occurred while getting display component: ", error);
    return null;
  }
};
</script>

<style scoped>
.media_attachment_wrapper {
  width: 300px;
  max-width: 300px;
  flex-shrink: 0;
}

.attachment_container {
  border: 1px solid var(--white-opacity-8);
  transition: background-color 0.4s ease-in-out;
  overflow: visible !important;
  position: relative;
  z-index: 1;
}

.attachment_container:hover {
  background-color: var(--white-opacity-4) !important;
  z-index: 2;
}

.action_container:hover {
  background-color: var(--white-opacity-4);
  transition: background-color 0.4s ease-in-out;
}

.remove_button {
  border-radius: 50px;
  border: 2px solid var(--layout-bg-color);
  background: var(--color-666);
  position: relative;
  z-index: 999;
}

.hide-scrollbar {
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
</style>
